/**
 * Every element Tab can land on inside a row this feature renders. A subset of
 * the full focusable-elements spec, not the general case — chosen for what
 * actually appears in a condition row, a level card, the toolbar or an
 * approvals queue row.
 *
 * Radios are excluded outright — a native `input[type=radio]` and an ARIA
 * `role="radio"` alike, the same two shapes `RuleEditorPanel`'s own
 * `scrollToFirstError` guards against on a level card. A level's only radios
 * are its Any / All approver-logic choice, and a radio group is the one
 * control where an arrow key changes the value as a side effect of merely
 * moving focus:
 * landing here after a removal would let the next ArrowDown silently flip
 * how many approvers the level requires, with no dialog and no undo. Unlike
 * `scrollToFirstError`, which genuinely wants a radio when the scope
 * section's own choice is the refused control, none of this helper's callers
 * ever wants one as the destination, so the exclusion is unconditional here.
 */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]):not([role="radio"]), input:not([disabled]):not([type="hidden"]):not([type="radio"]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([role="radio"])';

/**
 * Moves focus after the row that held it unmounts, instead of leaving the
 * browser to drop it on `<body>` — which is what happens by default when the
 * element that had focus is removed from the DOM, and otherwise loses a
 * keyboard user's place in the list entirely.
 *
 * Takes the DOM elements that outlive the removal, in the order they should be
 * preferred — typically the next row, then the previous one. Candidates are
 * containers (a row, a card), not the control to focus: the control inside
 * one is looked up *inside* the deferred callback, not now, and each
 * candidate is tried in turn rather than committing to the first non-null
 * reference — a container that turns out to hold nothing focusable must not
 * end the search early. Removing one row can change what the sibling itself
 * renders — a level card's own drag handle unmounts the moment removal
 * drops the rule to one level, since a single level takes no drag handle —
 * so a focusable element resolved before that re-render can already be gone
 * by the time the deferred call runs; re-querying after the DOM has settled
 * is what a stale reference cannot give.
 *
 * The fallback (an "Add …" control, for when no row is left) gets the same
 * late treatment, as a getter rather than a node: a condition builder whose
 * last authorable type is already spent renders no Add button at all
 * (`ConditionBuilder`'s `{nextType && (...)}`) — removing the only row is
 * what frees that type back up, so the button exists only *after* the
 * re-render this call is waiting for. Resolving it now, before the removal,
 * would miss the one moment it starts existing.
 *
 * Deferred with `setTimeout`, not `requestAnimationFrame`. React commits a
 * click or key handler's `setState` in a microtask after the handler returns,
 * so the removed row is still in the DOM while the handler runs, and gone by
 * the time any macrotask runs. The wait is only to get past that commit, not
 * to wait for a paint. rAF is paused entirely on a backgrounded or otherwise
 * non-rendering tab in a way a `setTimeout` macrotask is not.
 *
 * A `setTimeout(fn, 0)` has no upper bound on when it actually runs — it
 * queues behind whatever else is on the main thread, which under real load
 * (a heavier re-render, a Redux/Saga action, another timer) can be long
 * enough for the user to have already clicked or tabbed into something else
 * entirely. Focusing on top of that steals focus from work genuinely in
 * progress — closing an open react-select menu blurs it, which unmounts the
 * option list, so a pick made in that window silently lands on nothing.
 * `document.activeElement` still being `<body>` is what tells the callback
 * nothing has claimed focus since the removal; only then does it act.
 */
export const focusAfterRemoval = (
  candidates: (HTMLElement | null | undefined)[],
  fallback?: () => HTMLElement | null,
) => {
  setTimeout(() => {
    if (document.activeElement !== document.body) return;
    for (const container of candidates) {
      if (!container) continue;
      const focusable = container.matches(FOCUSABLE_SELECTOR)
        ? container
        : container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable) {
        focusable.focus();
        return;
      }
    }
    fallback?.()?.focus();
  }, 0);
};
